import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Checkbox, Button, HStack, useToast,
} from '@chakra-ui/react';
import {
  ImmBanavim, crearBanavim, actualizarBanavim,
} from '../../../api/immApi';
import {
  SectionTitle, Field, EstatusBadge, fmt, ExpedienteSelect,
  IMM_COLOR, IMM_COLOR_HOVER,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmBanavim;
  onGuardado?: (b: ImmBanavim) => void;
  onCancelar?: () => void;
}

const ESTATUS_OPCIONES = ['Registrado', 'Actualizado'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioBanavim: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId);

  const [f, setF] = useState<Partial<ImmBanavim>>({
    folioBanavim: data?.folioBanavim || '',
    fechaRegistro: data?.fechaRegistro ? data.fechaRegistro.split('T')[0] : hoy(),
    observaciones: data?.observaciones || '',
    canalizacionRealizada: data?.canalizacionRealizada ?? false,
    acompanamientoRealizado: data?.acompanamientoRealizado ?? false,
    estatus: data?.estatus || 'Registrado',
  });

  const set = (campo: keyof ImmBanavim, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  if (modo === 'view') {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos del registro BANAVIM</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Field label="Expediente" value={data?.expediente?.folio ?? (data ? `#${data.expedienteId}` : '—')} />
          <Field label="Folio BANAVIM" value={data?.folioBanavim} />
          <Field label="Fecha de registro" value={fmt(data?.fechaRegistro)} />
          <Field label="Estatus" value={<EstatusBadge value={data?.estatus} />} />
          <Field label="Canalización realizada" value={data?.canalizacionRealizada ? 'Sí' : 'No'} />
          <Field label="Acompañamiento realizado" value={data?.acompanamientoRealizado ? 'Sí' : 'No'} />
        </SimpleGrid>
        <SectionTitle>Observaciones</SectionTitle>
        <Field label="Observaciones" value={data?.observaciones} />
      </VStack>
    );
  }

  const handleGuardar = async () => {
    if (modo === 'new' && !expedienteId) {
      toast({ title: 'El expediente es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmBanavim> = {
        ...f,
        ...(modo === 'new' ? { expedienteId } : {}),
      };
      const res = modo === 'edit' && data
        ? await actualizarBanavim(data.id, payload)
        : await crearBanavim(payload);
      toast({ title: modo === 'edit' ? 'Registro actualizado' : 'Registro creado', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar el registro.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos del registro BANAVIM</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Expediente</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} isDisabled={modo !== 'new'} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Folio BANAVIM</FormLabel>
          <Input value={f.folioBanavim || ''} onChange={(e) => set('folioBanavim', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de registro</FormLabel>
          <Input type="date" value={(f.fechaRegistro as string) || ''} onChange={(e) => set('fechaRegistro', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Registrado'} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_OPCIONES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl display="flex" alignItems="center" gap={4} pt={{ md: 6 }}>
          <Checkbox isChecked={!!f.canalizacionRealizada} onChange={(e) => set('canalizacionRealizada', e.target.checked)}>
            Canalización realizada
          </Checkbox>
          <Checkbox isChecked={!!f.acompanamientoRealizado} onChange={(e) => set('acompanamientoRealizado', e.target.checked)}>
            Acompañamiento realizado
          </Checkbox>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={3} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar registro
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioBanavim;
